import { inject, Injectable, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Observable, Subject } from 'rxjs';
import { id } from '@wonder-core-ng/interfaces/id.type';
import { IChartDataEntry, IQueryEngineFilters } from './interfaces/query-engine.interface';
import { QueryEngineActions } from './query-engine.actions';
import {
  selectAllChartData,
  selectChartData,
  selectFailedCharts,
  selectHttpResult,
} from './query-engine.selectors';

@Injectable({
  providedIn: 'root',
})
export class QueryEngineService {
  private readonly store = inject(Store);

  constructor() {
    this.httpResult$
      .pipe(filter((result) => ([] as string[]).includes(result.action)))
      .subscribe(this.saveQueryEngineResult);
  }

  public get httpResult$() {
    return this.store.select(selectHttpResult);
  }

  public httpResult = this.store.selectSignal(selectHttpResult);

  private readonly saveQueryEngineResult = new Subject<{ action: string; error?: unknown }>();
  public get saveQueryEngineResult$() {
    return this.saveQueryEngineResult.asObservable();
  }

  public allChartData = this.store.selectSignal(selectAllChartData);

  public getChartData(chartId: id): Signal<IChartDataEntry | null> {
    return this.store.selectSignal(selectChartData(chartId));
  }

  public getChartData$(chartId: id): Observable<IChartDataEntry | null> {
    return this.store.select(selectChartData(chartId));
  }

  unloadChartData(chartId: id): void {
    this.store.dispatch(QueryEngineActions.unloadChartData({ chartId }));
  }

  /** Charts whose last request failed, by id. */
  readonly failedCharts = this.store.selectSignal(selectFailedCharts);

  loadChartData(query: IQueryEngineFilters): void {
    this.store.dispatch(QueryEngineActions.loadChartData({ query }));
  }

  formatCompanyName(name: string | undefined): string {
    if (!name) return '';

    const companyType: { regex: RegExp; correct: string }[] = [
      { regex: /\bS\.?\s?L\.?\s?U\.?$/iu, correct: 'S.L.U.' },
      { regex: /\bS\.?\s?A\.?\s?U\.?$/iu, correct: 'S.A.U.' },
      { regex: /\bS\.?\s?L\.?$/iu, correct: 'S.L.' },
      { regex: /\bS\.?\s?A\.?$/iu, correct: 'S.A.' },
      { regex: /\bS\.?\s?COOP\.?\s?V\.?$/iu, correct: 'S. Coop. V.' },
      { regex: /\bS\.?\s?COOP\.?$/iu, correct: 'S. Coop.' },
      { regex: /\bS\.?\s?V\.?\s?C\.?$/iu, correct: 'S.V.C.' },
      { regex: /\bSARL$/iu, correct: 'SARL' },
    ];

    let detectedType = '';
    let baseName = name.trim();

    for (const type of companyType) {
      if (type.regex.test(baseName)) {
        detectedType = type.correct;
        baseName = baseName.replace(type.regex, '').replace(/,\s*$/, '');
        break;
      }
    }

    const correctName = baseName
      .toLocaleLowerCase('es-ES')
      .split(/\s+/)
      .map((word) => {
        return word.charAt(0).toLocaleUpperCase('es-ES') + word.slice(1);
      })
      .join(' ');

    return detectedType ? `${correctName}, ${detectedType}` : correctName;
  }
}
